import * as React from "react";
import { Link, useHistory, useLocation } from "react-router-dom";

import {
  Badge,
  Card,
  CardBody,
  Page,
  PageHeader,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
} from "Components/ui";
import {
  ChevronRight,
  History,
  Settings2,
  ShieldCheck,
  Trash2,
  UserRound,
  Users,
} from "Components/ui/icons";
import { useAuth } from "hooks/useAuth";
import SapSettingsSheet from "pages/admin/sapSettings/SapSettingsSheet";
import LoginHistory from "./Components/LoginHistory";
import RecycleBinView from "./Components/RecycleBinView";
import { formatRelative, pluralize } from "utils/format";
import { PROFILE_PARAM } from "utils/profileSheet";
import { RECYCLE_RETENTION_DAYS } from "utils/recycleBin";
import { useSettingsData } from "./data";
import type { LegacySettingsPanel, SettingsLinkItem, SettingsPanel } from "./types";

const PANEL_PARAM = "panel";

const panelLink = (panel: SettingsPanel) => `/settings?${PANEL_PARAM}=${panel}`;

const readPanel = (search: string): SettingsPanel | null => {
  const value = new URLSearchParams(search).get(PANEL_PARAM);
  return value === "login" || value === "recycle" || value === "sap" ? value : null;
};

// Where `history.push("/settings", { panel })` used to land, now that users, roles and config have
// their own pages.
const LEGACY_PANEL_TARGETS: Record<LegacySettingsPanel, string> = {
  users: "/hr/users",
  roles: "/hr/roles",
  config: panelLink("sap"),
  login: panelLink("login"),
  recycle: panelLink("recycle"),
  sap: panelLink("sap"),
};

const SettingsLinkCard: React.FC<{ item: SettingsLinkItem }> = ({ item }) => {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      className="group block rounded-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <Card className="h-full transition-colors duration-fast ease-standard group-hover:border-input group-hover:bg-surface-raised">
        <CardBody className="flex h-full flex-col gap-4">
          <div className="flex items-start justify-between gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-primary-subtle text-primary-text">
              <Icon weight="duotone" aria-hidden="true" className="h-5 w-5" />
            </span>
            <ChevronRight
              aria-hidden="true"
              className="mt-1 h-4 w-4 text-subtle-foreground transition-colors duration-fast group-hover:text-muted-foreground"
            />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <h2 className="text-sm font-semibold text-foreground">{item.title}</h2>
            <p className="text-sm text-muted-foreground">{item.description}</p>
          </div>
          {item.badges && <div className="flex flex-wrap items-center gap-2">{item.badges}</div>}
        </CardBody>
      </Card>
    </Link>
  );
};

const SettingsPage: React.FC = () => {
  const location = useLocation<{ panel?: LegacySettingsPanel } | undefined>();
  const history = useHistory();
  const data = useSettingsData();
  const { user, hasPermission } = useAuth();
  const canSeeSapSettings =
    hasPermission("billing", "read") || hasPermission("tenantManagement", "read");
  const panel = readPanel(location.search);

  React.useEffect(() => {
    const requested = location.state?.panel;
    if (requested && LEGACY_PANEL_TARGETS[requested]) {
      history.replace(LEGACY_PANEL_TARGETS[requested]);
    }
  }, [location.state, history]);

  const closePanel = () => history.replace({ pathname: location.pathname });
  const onPanelOpenChange = (open: boolean) => {
    if (!open) {
      closePanel();
    }
  };

  let whatsAppBadge: React.ReactNode = null;
  if (user && !user.phoneNumber) {
    whatsAppBadge = (
      <Badge tone="warning" dot>
        No WhatsApp number
      </Badge>
    );
  } else if (user) {
    whatsAppBadge = user.phoneVerified ? (
      <Badge tone="success" dot>
        WhatsApp verified
      </Badge>
    ) : (
      <Badge tone="warning" dot>
        WhatsApp not verified
      </Badge>
    );
  }

  const items: SettingsLinkItem[] = [
    {
      key: "profile",
      title: "Your profile",
      description: "Your name, photo, WhatsApp number and password.",
      icon: UserRound,
      to: `${location.pathname}?${PROFILE_PARAM}=1`,
      badges: whatsAppBadge,
    },
  ];
  if (data.canSeeUsers) {
    items.push({
      key: "users",
      title: "Manage users",
      description: "Create, edit, suspend and reset access for admin users.",
      icon: Users,
      to: "/hr/users",
      badges: data.userCount !== null && (
        <>
          <Badge>{pluralize(data.userCount, "user")}</Badge>
          {data.activeUserCount !== null && (
            <Badge tone="success" dot>
              {data.activeUserCount} active
            </Badge>
          )}
        </>
      ),
    });
  }
  if (data.canSeeRoles) {
    items.push({
      key: "roles",
      title: "Roles and permissions",
      description: "Define roles and what each one can read or change.",
      icon: ShieldCheck,
      to: "/hr/roles",
      badges: data.roleCount !== null && <Badge>{pluralize(data.roleCount, "role")}</Badge>,
    });
  }
  items.push(
    {
      key: "login",
      title: "Login history",
      description: "Recent sign-ins to your account and where they came from.",
      icon: History,
      to: panelLink("login"),
      badges: data.lastSignIn && <Badge>Last sign-in {formatRelative(data.lastSignIn)}</Badge>,
    },
    ...(canSeeSapSettings
      ? [
          {
            key: "config",
            title: "SAP settings",
            description:
              "Pay pages and receipts, billing notifications, dashboard limits and integrations.",
            icon: Settings2,
            to: panelLink("sap"),
          },
        ]
      : []),
    {
      key: "recycle",
      title: "Recycle bin",
      description: `Restore deleted clients within ${RECYCLE_RETENTION_DAYS} days.`,
      icon: Trash2,
      to: panelLink("recycle"),
      badges: <Badge>{data.recycleCount ? pluralize(data.recycleCount, "item") : "Empty"}</Badge>,
    }
  );

  return (
    <Page>
      <PageHeader
        title="Settings"
        description="Your account, access control, sign-in activity and SAP's own settings."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <SettingsLinkCard key={item.key} item={item} />
        ))}
      </div>

      <Sheet open={panel === "login"} onOpenChange={onPanelOpenChange}>
        <SheetContent size="lg">
          <SheetHeader
            title="Login history"
            description="Recent sign-ins to your account, newest first."
          />
          <SheetBody>
            <LoginHistory
              entries={data.loginEntries}
              loading={data.loginsLoading}
              fallbackUserName={data.fallbackUserName}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      <SapSettingsSheet open={panel === "sap"} onOpenChange={onPanelOpenChange} />

      <Sheet open={panel === "recycle"} onOpenChange={onPanelOpenChange}>
        <SheetContent size="lg">
          <SheetHeader
            title="Recycle bin"
            description="Restore or permanently delete removed clients."
          />
          <SheetBody>
            <RecycleBinView />
          </SheetBody>
        </SheetContent>
      </Sheet>
    </Page>
  );
};

export default SettingsPage;
